import { GroupId } from '@mjolnir/mf-fields-ts/src/groups/GroupId';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import { ANALYTICS_EVENTS } from '@/bpc/utils/analyticsEvents';
import { OnboardingGroup } from '@/bpc/components/OnboardingGroupComponent/OnboardingGroup';
import { Group } from '@mjolnir/mf-fields-ts/src/types/Group.type';
import { EncryptedString } from '@mjolnir/mf-fields-ts/src/types/Field.type';
import { groups } from '@mjolnir/mf-fields-ts/src/groups/Groups';
import { logEvent } from '@/utils/analytics';
import { analytics } from 'moet-core';
import { OnboardingField } from '@/bpc/components/OnboardingFieldComponent/OnboardingField';
import { getFieldByAbsolutePath } from '@mjolnir/mf-fields-ts/src/types/FieldByAbsolutePath';

export class Onboarding {
  public static readonly ARRAY_INDEX_EXTRACTOR_PATTERN = /.*\[(\d)\]/;

  private readonly onboardingKrn: string;
  private readonly groupIndexes: Map<GroupId, number>;
  private readonly patchOnboarding: (payload: Payload) => Promise<void>;
  private readonly initiateUnderwriting: () => Promise<void>;
  private readonly additionalDataGroup: Group;
  private updatedFieldsForNextEvent: { [index: string]: string } = {};
  public readonly onboardingGroups: OnboardingGroup[];
  public payload: Payload;

  constructor(
    onboardingKrn: string,
    payload: Payload,
    patchOnboarding: (payload: Payload) => Promise<void>,
    initiateUnderwriting: () => Promise<void>,
  ) {
    this.onboardingKrn = onboardingKrn;
    this.payload = payload;
    this.patchOnboarding = patchOnboarding;
    this.initiateUnderwriting = initiateUnderwriting;
    const allGroups = groups(payload);
    const indexBillingBusinessPartner = allGroups.findIndex(
      group => group.id === GroupId.BillingBusinessPartner,
    );
    if (indexBillingBusinessPartner > -1) {
      allGroups.splice(indexBillingBusinessPartner, 1);
    }
    this.additionalDataGroup = allGroups.find(g => g.id === GroupId.AdditionalData) as Group;
    this.onboardingGroups = Onboarding.toSortedOnboardingGroups(this, allGroups, payload);
    this.groupIndexes = new Map(this.onboardingGroups.map((g, i) => [g.groupId, i]));
  }

  public firstGroup(): OnboardingGroup | undefined {
    const firstGroup = this.onboardingGroups.find(g => {
      /**
       * Edge case for Groups without fields
       */
      if (g.forceApplicable) {
        return true;
      }

      // default behaviour
      return g.isApplicable() && !g.allMandatoryFilled();
    });

    if (!firstGroup) {
      // eslint-disable-next-line no-console
      console.warn('There are no applicable onboarding groups for first group');
    }

    return firstGroup;
  }

  public lastGroup(): OnboardingGroup {
    const applicableGroups = this.onboardingGroups.filter(g => g.isApplicable());
    if (applicableGroups.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('There are no applicable onboarding groups for last group');
    }

    return applicableGroups[applicableGroups.length - 1];
  }

  public nextGroup(currentGroup: GroupId): OnboardingGroup {
    const currentGroupIndex = this.groupIndexes.get(currentGroup) as number;
    for (let i = currentGroupIndex + 1; i < this.onboardingGroups.length; i++) {
      if (this.onboardingGroups[i].isApplicable()) {
        return this.onboardingGroups[i];
      }
    }
    return this.onboardingGroups[currentGroupIndex];
  }

  public hasNextGroup(currentGroupId: GroupId): boolean {
    return this.nextGroup(currentGroupId).groupId !== currentGroupId;
  }

  public previousGroup(currentGroup: GroupId): OnboardingGroup {
    const currentIndex = this.groupIndexes.get(currentGroup) as number;
    for (let i = Math.max(0, currentIndex - 1); i >= 0; i--) {
      if (this.onboardingGroups[i].isApplicable()) {
        return this.onboardingGroups[i];
      }
    }

    return this.onboardingGroups[currentIndex];
  }

  public hasPreviousGroup(currentGroupId: GroupId): boolean {
    return this.previousGroup(currentGroupId).groupId !== currentGroupId;
  }

  public clearCache(): void {
    this.onboardingGroups.forEach(g => g.clearCache());
  }

  public async pushPayloadToBackendAndTriggerContinueEvent(currentGroupId: GroupId): Promise<void> {
    await this.pushPayloadToBackend();
    this.updatedFieldsForNextEvent.currentGroupId = currentGroupId;
    this.updatedFieldsForNextEvent.destinationGroupId = this.nextGroup(currentGroupId).groupId;
    this.logEvent(
      ANALYTICS_EVENTS.continueButton(Object.keys(this.updatedFieldsForNextEvent)),
      this.updatedFieldsForNextEvent,
    );
    this.updatedFieldsForNextEvent = {};
  }

  public pushPayloadToBackend(): Promise<any> {
    return this.patchOnboarding(this.payload);
  }

  public submitApplication(): Promise<void> {
    return this.initiateUnderwriting();
  }

  // @ts-expect-error: FIXME
  public logEvent(event: analytics.Event, customData: { [index: string]: string }): void {
    const enhancedCustomData = {
      ...customData,
      onboardingKrn: this.onboardingKrn,
      onboardingType: this.payload.contract_onboarding_type,
    };
    logEvent(event, enhancedCustomData);
  }

  public setValueOfField(
    field: OnboardingField,
    value: boolean | number | string | EncryptedString | string[],
  ): void {
    Onboarding.setObjectValueByPath(this.payload, field.absolutePath, value);
    this.updatedFieldsForNextEvent[field.absolutePath] = field.trackFieldValueWithAnalytics
      ? String(value)
      : 'MASKED_VALUE';
    this.clearCache();
  }

  public createMissingAdditionalDataGroup(missingAdditionalDataPaths: string[]): OnboardingGroup {
    const newAdditionalDataGroup = { ...this.additionalDataGroup };
    missingAdditionalDataPaths.map((data, i) => {
      newAdditionalDataGroup.fieldPathsOnPayload[`field${i}`] = data;
      newAdditionalDataGroup.fields[`field${i}`] = getFieldByAbsolutePath(data);
    });

    return new OnboardingGroup(this, newAdditionalDataGroup, this.payload);
  }

  private static toSortedOnboardingGroups = (
    onboarding: Onboarding,
    mfGroups: Group[],
    payload: Payload,
  ): OnboardingGroup[] => {
    return mfGroups
      .sort((a, b) => a.priority - b.priority)
      .map(g => new OnboardingGroup(onboarding, g, payload));
  };

  public static setObjectValueByPath = (
    target: Record<string, any>,
    absolutePath: string,
    value: boolean | number | string | EncryptedString | string[],
  ): void => {
    Onboarding.setValueByPathArray(target, absolutePath.split('.'), value);
  };

  public static setValueByPathArray = (
    target: Record<string, any>,
    pathParts: string[],
    value: boolean | number | string | EncryptedString | string[],
  ): void => {
    const currentPart = pathParts.shift() as string;
    const targetArrayIndex = Onboarding.extractArrayIndexFromFieldPath(currentPart);
    if (targetArrayIndex !== null) {
      const currentKey = Onboarding.ensurePrefilledArrayAndReturnKey(
        target,
        currentPart,
        targetArrayIndex,
      );
      if (pathParts.length === 0) {
        target[currentKey][targetArrayIndex] = value;
      } else {
        if (target[currentKey][targetArrayIndex] == null) {
          target[currentKey][targetArrayIndex] = {};
        }
        Onboarding.setValueByPathArray(target[currentKey][targetArrayIndex], pathParts, value);
      }
    } else {
      if (pathParts.length === 0) {
        target[currentPart] = value;
      } else {
        if (target[currentPart] == null) {
          target[currentPart] = {};
        }
        Onboarding.setValueByPathArray(target[currentPart], pathParts, value);
      }
    }
  };

  public static ensurePrefilledArrayAndReturnKey = (
    target: Record<string, any>,
    part: string,
    targetArrayIndex: number,
  ): string => {
    const currentKey = part.substring(0, part.indexOf('['));
    if (target[currentKey] == null) {
      target[currentKey] = [];
    }
    for (let i = target[currentKey].length; i < targetArrayIndex; i++) {
      target[currentKey].push(null);
    }
    return currentKey;
  };

  public static extractArrayIndexFromFieldPath = (part: string): number | null => {
    const extract = Onboarding.ARRAY_INDEX_EXTRACTOR_PATTERN.exec(part);
    return extract === null ? null : Number.parseInt(extract[1]);
  };

  public getContractOnboardingType(): string {
    return this.payload.contract_onboarding_type as string;
  }
}
